import { useEffect, useRef, useState } from 'react'

import type { CustomIndicator } from '../stores/customIndicatorStore'
import type { AttachedCustomIndicator } from '../types/chart'
import type { PlotDef, WorkerBars, WorkerRequest, WorkerResponse } from '../workers/indicatorWorker'

export type IndicatorResults = Map<string, PlotDef[]>

interface Bar {
  time: number
  open: number
  high: number
  low: number
  close: number
  value?: number
}

/**
 * Runs custom/Pine indicator code in a Web Worker for each enabled attached instance.
 * Returns Map<instanceId, PlotDef[]> so callers can render each instance independently.
 */
export function useCustomIndicatorValues(
  /** Per-chart attached instances (from chartStore panel.customInstances). */
  instances: AttachedCustomIndicator[],
  /** Definition lookup map built from customIndicatorStore. */
  definitions: Map<string, CustomIndicator>,
  bars: Bar[]
): { results: IndicatorResults; errors: Map<string, string[]> } {
  const workerRef = useRef<Worker | null>(null)
  const [results, setResults] = useState<IndicatorResults>(new Map())
  const [errors, setErrors] = useState<Map<string, string[]>>(new Map())

  useEffect(() => {
    workerRef.current = new Worker(new URL('../workers/indicatorWorker.ts', import.meta.url), {
      type: 'module',
    })

    workerRef.current.addEventListener('message', (e: MessageEvent<WorkerResponse>) => {
      const { id, plots, errors: errs } = e.data
      setResults((prev) => {
        const next = new Map(prev)
        next.set(id, plots)
        return next
      })
      if (errs.length > 0) {
        setErrors((prev) => {
          const next = new Map(prev)
          next.set(id, errs)
          return next
        })
      } else {
        setErrors((prev) => {
          const next = new Map(prev)
          next.delete(id)
          return next
        })
      }
    })

    return () => {
      workerRef.current?.terminate()
      workerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!workerRef.current || bars.length === 0) return

    const workerBars: WorkerBars = {
      t: bars.map((b) => b.time),
      o: bars.map((b) => b.open),
      h: bars.map((b) => b.high),
      l: bars.map((b) => b.low),
      c: bars.map((b) => b.close),
      v: bars.map((b) => b.value ?? 0),
    }

    const enabled = instances.filter((inst) => inst.enabled)

    for (const inst of enabled) {
      const def = definitions.get(inst.definitionId)
      if (!def) continue
      const req: WorkerRequest = {
        type: 'run',
        id: inst.instanceId,
        code: def.code,
        bars: workerBars,
      }
      workerRef.current.postMessage(req)
    }

    // Remove results for detached or disabled instances
    const enabledIds = new Set(enabled.map((i) => i.instanceId))
    setResults((prev) => {
      const next = new Map(prev)
      for (const key of next.keys()) {
        if (!enabledIds.has(key)) next.delete(key)
      }
      return next
    })
  }, [instances, definitions, bars])

  return { results, errors }
}
